import type { SourceContext } from "@/services/contextOrchestrator";
import { messageContentToText } from "@/lib/chatHistoryLimits";
import { htmlToPlainText } from "@shared/htmlToPlainText";

export type CitationRecord = Record<number, SourceContext>;

export interface ParsedCitation {
  index: number;
  startPos: number;
  endPos: number;
  source: SourceContext | null;
}

export function parseCitationsFromText(
  text: string,
  citationMap: CitationRecord
): ParsedCitation[] {
  const citations: ParsedCitation[] = [];
  const regex = /\[(\d+)\]/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const index = parseInt(match[1], 10);
    citations.push({
      index,
      startPos: match.index,
      endPos: match.index + match[0].length,
      source: citationMap[index] || null,
    });
  }

  return citations;
}

export function extractUsedCitations(
  text: string | unknown,
  citationMap: CitationRecord
): SourceContext[] {
  const citations = parseCitationsFromText(messageContentToText(text), citationMap);
  const usedSources: SourceContext[] = [];
  const seenIndices = new Set<number>();

  for (const citation of citations) {
    if (citation.source && !seenIndices.has(citation.index)) {
      usedSources.push(citation.source);
      seenIndices.add(citation.index);
    }
  }

  return usedSources.sort((a, b) => a.citationIndex - b.citationIndex);
}

export function mapToRecord(map: Map<number, SourceContext>): CitationRecord {
  const record: CitationRecord = {};
  map.forEach((value, key) => {
    record[key] = value;
  });
  return record;
}

/** Strip HTML tags and normalize whitespace for plain-text display */
export function stripHtml(html: string): string {
  return htmlToPlainText(html);
}

export function getSnippet(content: string, maxLength: number = 150): string {
  if (!content) return "";
  const cleaned = stripHtml(content);
  if (cleaned.length <= maxLength) return cleaned;
  return cleaned.substring(0, maxLength).trim() + "...";
}

export function getSourceTypeIcon(type: string): string {
  switch (type?.toLowerCase()) {
    case "url":
    case "website":
    case "web":
      return "globe";
    case "document":
    case "doc":
    case "file":
      return "file-text";
    case "youtube":
    case "video":
      return "video";
    case "pdf":
      return "file";
    case "github":
      return "github";
    case "google-drive":
    case "gdrive":
      return "folder";
    case "notion":
      return "book-open";
    default:
      return "file-text";
  }
}
